import { Static, Type } from '@sinclair/typebox';
import { PromotionPendingCondition } from './promotion-pricing';
import { ProPromoCodeCustomerScope } from './translation_namespace/pro_promo_code_namespace';

/**
 * Promotion affichée sur une prestation, commune aux cartes du tunnel et au site vitrine : tout est calculé
 * et formaté par le backend. Acquise (aucune condition) : prix de la formule de base avant / après, pour le
 * prix barré. Sous conditions (heures, lieux, membres, clients ciblés) : badge avec mention, sans prix barré
 * — la remise se vérifie dans le tunnel.
 */
export const PrestationPromotionFields = {
  label: Type.String(),
  // « −20 % », « −10,00 € » (formaté serveur).
  value_label: Type.String(),
  conditions: Type.Array(Type.Enum(PromotionPendingCondition)),
  customer_scope: Type.Optional(Type.Enum(ProPromoCodeCustomerScope)),
  price_before: Type.Optional(Type.String()),
  price_after: Type.Optional(Type.String()),
  // Promotion réservée à certains membres (lot 4) : badge sur leurs puces, au-dessus du calendrier.
  team_member_ids: Type.Optional(Type.Array(Type.Integer())),
};

/**
 * Valeurs machine d'un prix remisé (unité mineure, jamais affichées) : `Offer.price` du JSON-LD et paramètre
 * `discount` du suivi. L'affichage lit les prix formatés.
 */
const PromotionRawAmountFields = {
  price_after_minor: Type.Optional(Type.Integer()),
  discount_minor: Type.Optional(Type.Integer()),
};

/** Prix barré d'une formule de la fiche (pastilles durée / prix), quand la promotion est acquise. */
export const PublicPrestationPromotionPriceSchema = Type.Object({
  price_id: Type.Number(),
  price_before: Type.String(),
  price_after: Type.String(),
  ...PromotionRawAmountFields,
});
export type PublicPrestationPromotionPriceObject = Static<typeof PublicPrestationPromotionPriceSchema>;

/** Champs du site vitrine propres à une promotion, quelle que soit la prestation (bandeau, section Promotion). */
const SitePromotionFields = {
  promotion_id: Type.Number(),
  description: Type.Optional(Type.String()),
  // Promotion à code mise en avant : le code s'affiche (bouton Copier) et « Réserver » le transmet.
  code: Type.Optional(Type.String()),
  // Happy hours (« le lundi et le mardi, de 10h00 à 12h00 ») : le site ne barre jamais un prix qui dépend
  // de l'heure, la carte cite les plages et le tunnel marque les créneaux.
  hours_label: Type.Optional(Type.String()),
  // Prénoms des membres concernés (« avec Sophie »).
  team_member_names: Type.Optional(Type.Array(Type.String())),
  // Fin de la promotion (instant ISO, compte à rebours) et sa date formatée (« 30 nov. 2026 »).
  ends_at: Type.Optional(Type.String()),
  ends_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'ends_at', is_text_short: true } }),
  ),
  // Dernier jour (`YYYY-MM-DD`, fuseau du pro) : valeur machine, `priceValidUntil` du JSON-LD.
  ends_on: Type.Optional(Type.String()),
};

/**
 * Promotion d'une prestation du site vitrine (liste, fiche, mise en avant) : la plus avantageuse des
 * promotions mises en avant (`highlight_on_site`), automatiques ou à code partagé, ouvertes et actives.
 */
export const PublicPrestationPromotionSchema = Type.Object({
  ...PrestationPromotionFields,
  ...SitePromotionFields,
  // Formule de base (prix « à partir de » de la carte), si acquise.
  ...PromotionRawAmountFields,
  prices: Type.Optional(Type.Array(PublicPrestationPromotionPriceSchema)),
});
export type PublicPrestationPromotionObject = Static<typeof PublicPrestationPromotionSchema>;

/**
 * Prestations concernées d'une promotion (section « Offres du moment ») : toutes, sinon les noms des
 * catégories visées puis ceux des prestations visées hors de ces catégories, dans l'ordre du catalogue.
 * Seuls les noms affichés sur le site, sans identifiant.
 */
export const PublicPromotionTargetsSchema = Type.Object({
  all: Type.Boolean(),
  category_names: Type.Optional(Type.Array(Type.String())),
  prestation_names: Type.Optional(Type.Array(Type.String())),
});
export type PublicPromotionTargetsObject = Static<typeof PublicPromotionTargetsSchema>;

/**
 * Promotion mise en avant, sans prestation : « Promotion liée » du bandeau haut et de la section Promotion,
 * cartes de la section « Offres du moment ».
 */
export const PublicHighlightedPromotionSchema = Type.Object({
  label: PrestationPromotionFields.label,
  value_label: PrestationPromotionFields.value_label,
  conditions: PrestationPromotionFields.conditions,
  customer_scope: PrestationPromotionFields.customer_scope,
  ...SitePromotionFields,
  // Absent quand la promotion ne vise plus aucune prestation affichée sur le site.
  targets: Type.Optional(PublicPromotionTargetsSchema),
});
export type PublicHighlightedPromotionObject = Static<typeof PublicHighlightedPromotionSchema>;

export const PublicHighlightedPromotionsResponseSchema = Type.Object({
  promotions: Type.Array(PublicHighlightedPromotionSchema),
});
export type PublicHighlightedPromotionsResponseObject = Static<typeof PublicHighlightedPromotionsResponseSchema>;
